import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getDataAPI, getEffectiveUserId, postDataAPI } from '../lib/api'
import { normalizeList } from '../lib/normalize'
import Modal from '../components/ui/Modal'
import { makeCacheKey, readSessionCache, writeSessionCache } from '../lib/sessionCache'
import { parseLocalDate, toLocalDateKey, todayKey, localDateDisplay, shortDateDisplay } from '../lib/dateUtils'

interface Show {
  id: string
  date: string
  title: string
  venue: string
  city: string
  ticket_count: number
  tix_sold?: number
  raw?: any
  show_time?: string
  event_title?: string
  status?: string
  perf_date?: string
  guarantee?: string
  bonus?: string
  description?: string
  sellable_cap?: string
  capacity?: number
  offer_expires?: string
  tix_sold_percent?: number
}

interface Task {
  id: string
  title: string
  description?: string
  due_date?: string
  status: string
  priority?: string
  assigned_to_user_id?: string
  category?: 'shows' | 'music' | 'commerce' | 'finance' | 'tour-manager' | 'general'
}

interface FinancialEntry {
  id: string
  date: string
  type: 'revenue' | 'spend'
  amount: number
  description?: string
  source: string
  vendor?: string
  income_type?: string
  notes?: string
  invoice_total?: number
}

interface Song {
  id: string
  song_title?: string
  title?: string
  streams_total?: number
  streams_manual_override?: number
  streams_today?: number
  release_date?: string
  artist_name?: string
  status?: string
}

interface Order {
  id: string
  order_number: string
  order_date: string
  total: number
  status: string
}

interface CalendarEvent {
  id: string
  title: string
  start: string
  location?: string
  description?: string
}

interface ScheduleItem {
  id: string
  type: 'show' | 'task' | 'release' | 'flight' | 'hotel' | 'calendar' | 'transport'
  title: string
  time?: string
  location?: string
  date: string
  details?: string
}

interface ActionItem {
  id: string
  category: 'task' | 'needs-attention' | 'at-risk'
  severity: 'critical' | 'medium'
  source: 'show' | 'music' | 'task'
  title: string
  description: string
  link?: string
  dueDate?: string
  itemId?: string
  actionType?: string
  relatedTo?: 'shows' | 'music' | 'commerce' | 'finance' | 'tour-manager' | 'general'
}

interface ActionGroup {
  type: string
  category: 'task' | 'needs-attention' | 'at-risk'
  label: string
  count: number
  items: ActionItem[]
}

interface SectionConfig {
  id: string
  label: string
  enabled: boolean
  order: number
}

// Default layout: Overview (hero) and Actions visible and listed back-to-back; rest hidden
const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'hero', label: 'Overview', enabled: true, order: 0 },
  { id: 'actions', label: 'Actions', enabled: true, order: 1 },
  { id: 'notes', label: 'Notes', enabled: false, order: 2 },
  { id: 'schedule', label: 'Schedule', enabled: false, order: 3 },
  { id: 'upcoming-shows', label: 'Upcoming Shows', enabled: false, order: 4 },
  { id: 'past-shows', label: 'Past Shows', enabled: false, order: 5 },
  { id: 'offers', label: 'Offers Expiring Next', enabled: false, order: 6 },
  { id: 'music', label: 'Music Performance', enabled: false, order: 7 },
  { id: 'tickets', label: 'Tickets', enabled: false, order: 8 },
  { id: 'commerce', label: 'Commerce Snapshot', enabled: false, order: 9 },
  { id: 'finance', label: 'Finance Snapshot', enabled: false, order: 10 },
]

function getToday() {
  return todayKey()
}

function getWeekStart() {
  const today = new Date()
  const day = today.getDay()
  const diff = today.getDate() - day
  const ws = new Date(today.getFullYear(), today.getMonth(), diff)
  return toLocalDateKey(ws)
}

function getMonthStart() {
  const today = new Date()
  return toLocalDateKey(new Date(today.getFullYear(), today.getMonth(), 1))
}

function getTomorrow() {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return toLocalDateKey(tomorrow)
}

// Mock data for demonstration
/* Unused mock data - kept for future use
const MOCK_SCHEDULE_TODAY: ScheduleItem[] = [
  {
    id: 'mock-1',
    type: 'show',
    title: 'Live Performance at The Roxy',
    time: '8:00 PM',
    location: 'The Roxy Theatre • Los Angeles, CA',
    date: getToday(),
    details: '450 tickets sold'
  },
  {
    id: 'mock-2',
    type: 'task',
    title: 'Approve social media posts',
    time: '2:00 PM',
    date: getToday(),
    details: 'high priority'
  },
  {
    id: 'mock-3',
    type: 'calendar',
    title: 'Meeting with Label',
    time: '11:00 AM',
    location: 'Zoom',
    date: getToday()
  }
]

const MOCK_SCHEDULE_TOMORROW: ScheduleItem[] = [
  {
    id: 'mock-4',
    type: 'flight',
    title: 'Flight to New York',
    time: '6:30 AM',
    location: 'LAX → JFK',
    date: getTomorrow(),
    details: 'Delta 1234'
  },
  {
    id: 'mock-5',
    type: 'transport',
    title: 'Car pickup to hotel',
    time: '12:30 PM',
    location: 'JFK Airport',
    date: getTomorrow(),
    details: 'Black SUV - License: ABC123'
  },
  {
    id: 'mock-6',
    type: 'hotel',
    title: 'Check-in at The Plaza Hotel',
    time: '3:00 PM',
    location: 'New York, NY',
    date: getTomorrow(),
    details: 'Booking #HTL-9821'
  },
  {
    id: 'mock-7',
    type: 'release',
    title: 'Release: Midnight Dreams (Single)',
    date: getTomorrow()
  },
  {
    id: 'mock-8',
    type: 'task',
    title: 'Review merchandise designs',
    time: '4:00 PM',
    date: getTomorrow(),
    details: 'medium priority'
  }
]
*/

// Mock offers data
const MOCK_OFFERS: Show[] = [
  {
    id: 'mock-offer-1',
    date: '2026-02-15',
    perf_date: '2026-02-15',
    title: 'Valentine\'s Weekend Special',
    venue: 'The Fillmore',
    city: 'San Francisco',
    ticket_count: 0,
    status: 'pending',
    guarantee: '$15,000',
    bonus: '$2,500 after 800 tickets',
    description: 'Co-headline with local artist',
    sellable_cap: '1,200',
    capacity: 1200,
    offer_expires: '2026-02-05'
  },
  {
    id: 'mock-offer-2',
    date: '2026-03-01',
    perf_date: '2026-03-01',
    title: 'Spring Festival',
    venue: 'Red Rocks Amphitheatre',
    city: 'Morrison',
    ticket_count: 0,
    status: 'pending',
    guarantee: '$25,000',
    bonus: '$5,000 after 1,500 tickets',
    description: 'Opening slot for headliner',
    sellable_cap: '9,525',
    capacity: 9525,
    offer_expires: '2026-02-08'
  },
  {
    id: 'mock-offer-3',
    date: '2026-03-12',
    perf_date: '2026-03-12',
    title: 'College Spring Concert',
    venue: 'University Arena',
    city: 'Austin',
    ticket_count: 0,
    status: 'pending',
    guarantee: '$12,000',
    bonus: '$1,500 after 600 tickets',
    description: 'Student activities board event',
    sellable_cap: '800',
    capacity: 800,
    offer_expires: '2026-02-10'
  },
  {
    id: 'mock-offer-4',
    date: '2026-03-20',
    perf_date: '2026-03-20',
    title: 'Downtown Music Series',
    venue: 'Brooklyn Steel',
    city: 'Brooklyn',
    ticket_count: 0,
    status: 'pending',
    guarantee: '$18,000',
    bonus: '$3,000 after 1,000 tickets',
    description: 'Prime Saturday night slot',
    sellable_cap: '1,800',
    capacity: 1800,
    offer_expires: '2026-02-12'
  },
  {
    id: 'mock-offer-5',
    date: '2026-04-05',
    perf_date: '2026-04-05',
    title: 'Spring Break Beach Bash',
    venue: 'Sunset Cove Amphitheater',
    city: 'Miami Beach',
    ticket_count: 0,
    status: 'pending',
    guarantee: '$20,000',
    bonus: '$4,000 after 2,000 tickets',
    description: 'Outdoor festival setting',
    sellable_cap: '3,000',
    capacity: 3000,
    offer_expires: '2026-02-15'
  }
]

// Mock financial entries
const MOCK_FINANCIAL_ENTRIES: FinancialEntry[] = [
  {
    id: 'mock-fin-1',
    date: '2026-01-25',
    type: 'revenue',
    amount: 15000,
    description: 'Show Settlement - Brooklyn Steel',
    source: 'Live Performance',
    vendor: 'Brooklyn Steel',
    income_type: 'revenue',
    notes: 'Final payment received',
    invoice_total: 15000
  },
  {
    id: 'mock-fin-2',
    date: '2026-01-24',
    type: 'spend',
    amount: 3500,
    description: 'Tour Bus Rental',
    source: 'Transportation',
    vendor: 'Premier Coach Lines',
    income_type: 'spend',
    notes: 'Week 1 of tour',
    invoice_total: 3500
  },
  {
    id: 'mock-fin-3',
    date: '2026-01-23',
    type: 'revenue',
    amount: 8750,
    description: 'Streaming Revenue - Q4 2025',
    source: 'Digital Distribution',
    vendor: 'Spotify',
    income_type: 'revenue',
    notes: 'Quarterly payout',
    invoice_total: 8750
  },
  {
    id: 'mock-fin-4',
    date: '2026-01-22',
    type: 'spend',
    amount: 1200,
    description: 'Studio Session',
    source: 'Recording',
    vendor: 'Electric Lady Studios',
    income_type: 'spend',
    notes: 'New single recording',
    invoice_total: 1200
  },
  {
    id: 'mock-fin-5',
    date: '2026-01-20',
    type: 'revenue',
    amount: 4500,
    description: 'Merchandise Sales',
    source: 'Merch',
    vendor: 'Online Store',
    income_type: 'revenue',
    notes: 'Weekly online sales',
    invoice_total: 4500
  },
  {
    id: 'mock-fin-6',
    date: '2026-01-19',
    type: 'spend',
    amount: 2800,
    description: 'Marketing Campaign',
    source: 'Advertising',
    vendor: 'Meta Ads',
    income_type: 'spend',
    notes: 'Instagram/Facebook promo',
    invoice_total: 2800
  },
  {
    id: 'mock-fin-7',
    date: '2026-01-18',
    type: 'revenue',
    amount: 12000,
    description: 'Show Settlement - The Fillmore',
    source: 'Live Performance',
    vendor: 'The Fillmore',
    income_type: 'revenue',
    notes: 'Sold out show',
    invoice_total: 12000
  },
  {
    id: 'mock-fin-8',
    date: '2026-01-17',
    type: 'spend',
    amount: 850,
    description: 'Hotel Accommodation',
    source: 'Lodging',
    vendor: 'Marriott Hotels',
    income_type: 'spend',
    notes: '3 nights for crew',
    invoice_total: 850
  },
  {
    id: 'mock-fin-9',
    date: '2026-01-15',
    type: 'revenue',
    amount: 6200,
    description: 'Sync License Fee',
    source: 'Licensing',
    vendor: 'Netflix',
    income_type: 'revenue',
    notes: 'TV series placement',
    invoice_total: 6200
  },
  {
    id: 'mock-fin-10',
    date: '2026-01-14',
    type: 'spend',
    amount: 1500,
    description: 'Equipment Rental',
    source: 'Production',
    vendor: 'SoundWorks Gear',
    income_type: 'spend',
    notes: 'Tour audio equipment',
    invoice_total: 1500
  }
]

export default function OverviewPage() {
  const userId = getEffectiveUserId() || 'self'
  const [upcomingShows, setUpcomingShows] = useState<Show[]>([])
  const [pastShows, setPastShows] = useState<Show[]>([])
  const [allShows, setAllShows] = useState<Show[]>([])
  const [songs, setSongs] = useState<Song[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [financialEntries, setFinancialEntries] = useState<FinancialEntry[]>([])
  // const [pendingTasks, setPendingTasks] = useState<Task[]>([])
  // const [allTasks, setAllTasks] = useState<Task[]>([])
  // const [users, setUsers] = useState<User[]>([])
  const [note, setNote] = useState('')
  const [_isLoading, setIsLoading] = useState(true)
  // const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
  const [scheduleToday, setScheduleToday] = useState<ScheduleItem[]>([])
  const [scheduleTomorrow, setScheduleTomorrow] = useState<ScheduleItem[]>([])
  const [actionItems, setActionItems] = useState<ActionItem[]>([])
  const [actionGroups, setActionGroups] = useState<ActionGroup[]>([])
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [selectedActionGroup, setSelectedActionGroup] = useState<ActionGroup | null>(null)
  
  // Layout customization states
  const [sections, setSections] = useState<SectionConfig[]>(DEFAULT_SECTIONS)
  const [isLayoutDropdownOpen, setIsLayoutDropdownOpen] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Merge saved layout with DEFAULT_SECTIONS so new sections (e.g. hero) always appear and order is canonical
  const mergeWithDefaults = (saved: SectionConfig[]): SectionConfig[] => {
    const defaultOrderById = new Map(DEFAULT_SECTIONS.map((s, i) => [s.id, i]))
    const byId = new Map<string, SectionConfig>()
    for (const s of saved) {
      byId.set(s.id, { ...s, order: defaultOrderById.get(s.id) ?? 999 })
    }
    for (const def of DEFAULT_SECTIONS) {
      if (!byId.has(def.id)) byId.set(def.id, { ...def })
    }
    const merged = Array.from(byId.values()).sort((a, b) => a.order - b.order)
    return merged.map((s, i) => ({ ...s, order: i }))
  }
  
  // Load saved layout from backend on mount
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const response = await getDataAPI('/user-settings/overview-layout')
        if (response?.data?.layout) {
          setSections(mergeWithDefaults(response.data.layout))
          return
        }
      } catch (error) {
        console.error('Failed to load layout:', error)
      }
        const saved = localStorage.getItem('overview-layout')
        if (saved) {
          try {
          setSections(mergeWithDefaults(JSON.parse(saved)))
          } catch (e) {
            console.error('Failed to parse saved layout')
        }
      }
    }
    loadLayout()
  }, [])
  
  // Save layout to backend when it changes
  useEffect(() => {
    const saveLayout = async () => {
      try {
        await postDataAPI('/user-settings/overview-layout', { layout: sections })
        // Also save to localStorage as backup
        localStorage.setItem('overview-layout', JSON.stringify(sections))
      } catch (error) {
        console.error('Failed to save layout:', error)
        // Still save to localStorage even if backend fails
        localStorage.setItem('overview-layout', JSON.stringify(sections))
      }
    }
    
    // Don't save on initial mount, only on changes
    if (sections !== DEFAULT_SECTIONS) {
      saveLayout()
    }
  }, [sections])
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLayoutDropdownOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const toggleSection = (id: string) => {
    setSections(prev => prev.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    ))
  }
  
  const moveSection = (fromIndex: number, toIndex: number) => {
    const newSections = [...sections]
    const [moved] = newSections.splice(fromIndex, 1)
    newSections.splice(toIndex, 0, moved)
    
    // Update order property
    const reordered = newSections.map((s, i) => ({ ...s, order: i }))
    setSections(reordered)
  }
  
  const resetLayout = async () => {
    setSections(DEFAULT_SECTIONS)
    try {
      await postDataAPI('/user-settings/overview-layout', { layout: DEFAULT_SECTIONS })
      localStorage.removeItem('overview-layout')
    } catch (error) {
      console.error('Failed to reset layout:', error)
      localStorage.removeItem('overview-layout')
    }
  }
  
  // Helper to render sections in the correct order
  const renderSection = (sectionId: string, content: React.ReactNode) => {
    const section = sections.find(s => s.id === sectionId)
    if (!section || !section.enabled) return null
    return (
      <div key={sectionId} style={{ order: section.order }}>
        {content}
      </div>
    )
  }

  // User display name for hero (matches legacy dashboard)
  const getOverviewUserName = () => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      if (!raw) return 'User'
      const user = JSON.parse(raw)
      return user?.name || user?.username || user?.email?.split('@')[0] || 'User'
    } catch {
      return 'User'
    }
  }

  const extractArray = (payload: any) => {
    const list = payload?.data?.data || payload?.data || payload || []
    if (Array.isArray(list)) return list
    if (Array.isArray(list?.data)) return list.data
    return []
  }

  const normalizeBookings = (payload: any): Show[] => {
    const array = extractArray(payload)
    return array.map((item: any) => ({
      id: item._id || item.id,
      date: item.date || item.perf_date,
      perf_date: item.perf_date || item.date,
      title: item.event_title || item.title || item.venue,
      event_title: item.event_title || item.title,
      venue: item.venue,
      city: item.city,
      tix_sold: item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0,
      ticket_count:
        item.ticket_count ??
        item.tickets_sold ??
        item.tix_sold ??
        item.current_ticket_count ??
        0,
      raw: item,
      show_time: item.show_time,
      status: item.status,
      guarantee: item.guarantee,
      bonus: item.bonus,
      description: item.description || item.event_description,
      sellable_cap: item.sellable_cap,
      capacity: item.capacity,
      offer_expires: item.offer_expires,
    }))
  }

  const toOptionalNumber = (value: any) => {
    if (value === null || value === undefined || value === '') return undefined
    const asNumber = Number(value)
    return Number.isNaN(asNumber) ? undefined : asNumber
  }

  const normalizeCatalog = (payload: any): Song[] => {
    const array = extractArray(payload)
    return array.map((item: any) => {
      const rawStreamsTotal =
        item.streams_total ??
        item.streams ??
        item.streamsTotal ??
        item?.soundcharts_data?.streams ??
        item?.soundcharts_data?.monthly_listeners ??
        0
      const rawStreamsToday =
        item.streams_today ??
        item.streamsToday ??
        item.streams_daily ??
        item.streamsDaily ??
        item.daily_streams ??
        item.dailyStreams

      return {
        id: item._id || item.id,
        song_title: item.song_title || item.title || item.songName,
        title: item.title || item.song_title,
        streams_total: Number(rawStreamsTotal) || 0,
        streams_manual_override: item.streams_manual_override || item.streamsManualOverride,
        streams_today: toOptionalNumber(rawStreamsToday),
        release_date: item.release_date || item.releaseDate,
        artist_name: item.artist_name || item.artistName || item.artist,
      }
    })
  }

  const evaluateActions = (
    shows: Show[],
    songs: Song[],
    tasks: Task[]
  ): ActionItem[] => {
    const actions: ActionItem[] = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    // Helper to get days until date (local-date safe)
    const getDaysUntil = (dateStr: string) => {
      const date = parseLocalDate(dateStr)
      if (!date) return Number.MAX_SAFE_INTEGER
      date.setHours(0, 0, 0, 0)
      return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    }

    // ============ TASKS (Due Today or Overdue) ============
    tasks.forEach((task) => {
      if (task.status !== 'pending' || !task.due_date) return
      
      const daysUntil = getDaysUntil(task.due_date)
      if (daysUntil <= 0) {
        actions.push({
          id: `task-${task.id}`,
          category: 'task',
          severity: daysUntil < 0 ? 'critical' : 'medium',
          source: 'task',
          title: task.title,
          description: daysUntil < 0 ? `Overdue by ${Math.abs(daysUntil)} days` : 'Due today',
          link: '/tasks',
          dueDate: task.due_date,
          relatedTo: task.category || 'general'
        })
      }
    })

    // ============ SHOWS - NEEDS ATTENTION ============
    shows.forEach((show) => {
      const showDate = show.date || show.perf_date
      if (!showDate) return
      
      const daysUntil = getDaysUntil(showDate)
      const raw = (show as any).raw || {}
      
      // High Priority - Missing critical info
      if ((show.status === 'confirmed' || show.status === 'announced') && !raw.guarantee) {
        actions.push({
          id: `show-guarantee-${show.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-guarantee'
        })
      }

      if (raw.deal_type && raw.deal_type.includes('split') && !raw.split_point) {
        actions.push({
          id: `show-split-${show.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-split-point'
        })
      }

      if (show.status === 'confirmed' && !raw.promoter_contact_emails && !raw.promoter_contact_names) {
        actions.push({
          id: `show-contact-${show.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-promoter-contact'
        })
      }

      if (show.status === 'announced' && !show.sellable_cap) {
        actions.push({
          id: `show-capacity-${show.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-capacity'
        })
      }

      if (raw.deposit_due && (parseLocalDate(raw.deposit_due) || new Date(0)) < today && !raw.deposit_received) {
        actions.push({
          id: `show-deposit-${show.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Deposit was due ${shortDateDisplay(raw.deposit_due)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'overdue-deposit'
        })
      }

      // Medium Priority
      if (daysUntil <= 14 && !raw.show_time) {
        actions.push({
          id: `show-time-${show.id}`,
          category: 'needs-attention',
          severity: 'medium',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)} in ${daysUntil} days`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-show-time'
        })
      }

      if (raw.status === 'on-sale' && !raw.ticket_prices_tiers) {
        actions.push({
          id: `show-prices-${show.id}`,
          category: 'needs-attention',
          severity: 'medium',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-ticket-prices'
        })
      }

      if (daysUntil <= 30 && !raw.advanced_info) {
        actions.push({
          id: `show-advance-${show.id}`,
          category: 'needs-attention',
          severity: 'medium',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)} in ${daysUntil} days`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-advance-info'
        })
      }

      if (daysUntil <= 7 && !raw.schedule) {
        actions.push({
          id: `show-schedule-${show.id}`,
          category: 'needs-attention',
          severity: 'medium',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show on ${shortDateDisplay(showDate)} in ${daysUntil} days`,
          link: '/shows',
          itemId: show.id,
          actionType: 'missing-schedule'
        })
      }
    })

    // ============ SHOWS - AT RISK ============
    shows.forEach((show) => {
      const showDate = show.date || show.perf_date
      if (!showDate) return
      
      const daysUntil = getDaysUntil(showDate)
      const raw = (show as any).raw || {}

      // Critical Risk
      if (daysUntil === 1) {
        // Check for missing travel (would need tour manager data)
        actions.push({
          id: `show-risk-tomorrow-${show.id}`,
          category: 'at-risk',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Show tomorrow - ${shortDateDisplay(showDate)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'show-tomorrow'
        })
      }

      if (daysUntil <= 3 && show.tix_sold_percent && show.tix_sold_percent < 30) {
        actions.push({
          id: `show-risk-tickets-${show.id}`,
          category: 'at-risk',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `${show.tix_sold_percent}% sold, ${daysUntil} days until show`,
          link: '/shows',
          itemId: show.id,
          actionType: 'low-ticket-sales'
        })
      }

      if (raw.red_flag) {
        actions.push({
          id: `show-risk-redflag-${show.id}`,
          category: 'at-risk',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: raw.red_flag_notes || 'Show marked as red flag',
          link: '/shows',
          itemId: show.id,
          actionType: 'red-flag'
        })
      }

      if (raw.deposit_due && (parseLocalDate(raw.deposit_due) || new Date(0)) < today && !raw.deposit_received) {
        actions.push({
          id: `show-risk-deposit-${show.id}`,
          category: 'at-risk',
          severity: 'critical',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `Deposit overdue since ${shortDateDisplay(raw.deposit_due)}`,
          link: '/shows',
          itemId: show.id,
          actionType: 'overdue-deposit-risk'
        })
      }

      // Medium Risk
      if (daysUntil <= 30 && show.tix_sold_percent && show.tix_sold_percent < 50) {
        actions.push({
          id: `show-risk-sales-${show.id}`,
          category: 'at-risk',
          severity: 'medium',
          source: 'show',
          title: `${show.title || show.venue}`,
          description: `${show.tix_sold_percent}% sold with ${daysUntil} days to go`,
          link: '/shows',
          itemId: show.id,
          actionType: 'below-target-sales'
        })
      }
    })

    // ============ MUSIC - NEEDS ATTENTION ============
    songs.forEach((song) => {
      if (song.status !== 'unreleased' && song.status !== 'demo') return
      if (!song.release_date) return
      
      const daysUntil = getDaysUntil(song.release_date)
      if (daysUntil < 0) return // Ignore past releases
      
      const raw = (song as any).raw || {}
      
      // Check for missing splits
      const hasMasterContributors = raw.master_contributors && raw.master_contributors.length > 0
      const hasPublishingContributors = raw.publishing_contributors && raw.publishing_contributors.length > 0
      
      if (!hasMasterContributors) {
        actions.push({
          id: `music-master-${song.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'music',
          title: `${song.song_title || song.title}`,
          description: `Releasing ${shortDateDisplay(song.release_date)} in ${daysUntil} days`,
          link: '/music',
          itemId: song.id,
          actionType: 'missing-master-splits'
        })
      }

      if (!hasPublishingContributors) {
        actions.push({
          id: `music-publishing-${song.id}`,
          category: 'needs-attention',
          severity: 'critical',
          source: 'music',
          title: `${song.song_title || song.title}`,
          description: `Releasing ${shortDateDisplay(song.release_date)} in ${daysUntil} days`,
          link: '/music',
          itemId: song.id,
          actionType: 'missing-publishing-splits'
        })
      }
    })

    // ============ MUSIC - AT RISK ============
    songs.forEach((song) => {
      if (song.status !== 'unreleased' && song.status !== 'demo') return
      if (!song.release_date) return
      
      const daysUntil = getDaysUntil(song.release_date)
      if (daysUntil < 0) return
      
      const raw = (song as any).raw || {}
      
      // PRO registration warnings
      if (!raw.registered_to_pro) {
        if (daysUntil <= 30) {
          actions.push({
            id: `music-pro-${daysUntil}-${song.id}`,
            category: 'at-risk',
            severity: 'medium',
            source: 'music',
            title: `${song.song_title || song.title}`,
            description: `Releasing ${shortDateDisplay(song.release_date)} in ${daysUntil} days`,
            link: '/music',
            itemId: song.id,
            actionType: 'no-pro-registration'
          })
        }
      }
    })

    // ============ MOCK DATA FOR DEMONSTRATION ============
    // Set to false when you have real data
    const USE_MOCK_DATA = true
    
    if (USE_MOCK_DATA) {
      // Tasks - Mock Items
    actions.push({
      id: 'mock-task-1',
      category: 'task',
      severity: 'critical',
      source: 'task',
      title: 'Update tour rider',
      description: 'Overdue by 2 days',
      link: '/tasks',
      dueDate: '2026-01-24',
      relatedTo: 'shows'
    })
    
    actions.push({
      id: 'mock-task-2',
      category: 'task',
      severity: 'medium',
      source: 'task',
      title: 'Confirm merch order',
      description: 'Due today',
      link: '/tasks',
      dueDate: '2026-01-26',
      relatedTo: 'commerce'
    })
    
    actions.push({
      id: 'mock-task-3',
      category: 'task',
      severity: 'critical',
      source: 'task',
      title: 'Submit PRO registration',
      description: 'Overdue by 5 days',
      link: '/tasks',
      dueDate: '2026-01-21',
      relatedTo: 'music'
    })

    // At Risk - Mock Items
    actions.push({
      id: 'mock-at-risk-1',
      category: 'at-risk',
      severity: 'critical',
      source: 'show',
      title: 'Rogers Centre',
      description: 'Show tomorrow - 1/27/2026',
      link: '/shows',
      itemId: 'mock-show-1',
      actionType: 'show-tomorrow'
    })
    
    actions.push({
      id: 'mock-at-risk-2',
      category: 'at-risk',
      severity: 'critical',
      source: 'show',
      title: 'Madison Square Garden',
      description: '28% sold, 2 days until show',
      link: '/shows',
      itemId: 'mock-show-2',
      actionType: 'low-ticket-sales'
    })
    
    actions.push({
      id: 'mock-at-risk-3',
      category: 'at-risk',
      severity: 'medium',
      source: 'show',
      title: 'The Forum',
      description: '45% sold with 15 days to go',
      link: '/shows',
      itemId: 'mock-show-3',
      actionType: 'below-target-sales'
    })
    
    actions.push({
      id: 'mock-at-risk-4',
      category: 'at-risk',
      severity: 'medium',
      source: 'music',
      title: 'Summer Nights',
      description: 'Releasing 2/10/2026 in 15 days',
      link: '/music',
      itemId: 'mock-song-1',
      actionType: 'no-pro-registration'
    })
    
    // Add more at-risk items
    for (let i = 1; i <= 8; i++) {
      actions.push({
        id: `mock-at-risk-sales-${i}`,
        category: 'at-risk',
        severity: 'medium',
        source: 'show',
        title: `Concert Hall ${i}`,
        description: `${30 + i}% sold with ${20 + i} days to go`,
        link: '/shows',
        itemId: `mock-show-sales-${i}`,
        actionType: 'below-target-sales'
      })
    }
    
    for (let i = 1; i <= 5; i++) {
      actions.push({
        id: `mock-at-risk-tickets-${i}`,
        category: 'at-risk',
        severity: 'critical',
        source: 'show',
        title: `Arena ${i}`,
        description: `${15 + i}% sold, ${3 + i} days until show`,
        link: '/shows',
        itemId: `mock-show-tickets-${i}`,
        actionType: 'low-ticket-sales'
      })
    }
    
    for (let i = 1; i <= 12; i++) {
      actions.push({
        id: `mock-at-risk-pro-${i}`,
        category: 'at-risk',
        severity: 'medium',
        source: 'music',
        title: `Track ${i}`,
        description: `Releasing ${new Date(2026, 1, i + 10).toLocaleDateString()} in ${i + 10} days`,
        link: '/music',
        itemId: `mock-song-pro-${i}`,
        actionType: 'no-pro-registration'
      })
    }

    // Needs Attention - Mock Items
    actions.push({
      id: 'mock-needs-1',
      category: 'needs-attention',
      severity: 'critical',
      source: 'show',
      title: 'Staples Center',
      description: 'Show on 2/15/2026',
      link: '/shows',
      itemId: 'mock-show-4',
      actionType: 'missing-guarantee'
    })
    
    actions.push({
      id: 'mock-needs-2',
      category: 'needs-attention',
      severity: 'critical',
      source: 'show',
      title: 'Red Rocks Amphitheatre',
      description: 'Show on 3/1/2026',
      link: '/shows',
      itemId: 'mock-show-5',
      actionType: 'missing-promoter-contact'
    })
    
    actions.push({
      id: 'mock-needs-3',
      category: 'needs-attention',
      severity: 'medium',
      source: 'show',
      title: 'The Wiltern',
      description: 'Show on 2/20/2026 in 25 days',
      link: '/shows',
      itemId: 'mock-show-6',
      actionType: 'missing-show-time'
    })
    
    actions.push({
      id: 'mock-needs-4',
      category: 'needs-attention',
      severity: 'medium',
      source: 'show',
      title: 'House of Blues',
      description: 'Show on 3/5/2026 in 38 days',
      link: '/shows',
      itemId: 'mock-show-7',
      actionType: 'missing-advance-info'
    })
    
    // Add more mock items to increase counts
    for (let i = 1; i <= 35; i++) {
      actions.push({
        id: `mock-missing-time-${i}`,
        category: 'needs-attention',
        severity: 'medium',
        source: 'show',
        title: `Venue ${i}`,
        description: `Show on ${new Date(2026, 1, i).toLocaleDateString()} in ${i} days`,
        link: '/shows',
        itemId: `mock-show-time-${i}`,
        actionType: 'missing-show-time'
      })
    }
    
    for (let i = 1; i <= 37; i++) {
      actions.push({
        id: `mock-missing-advance-${i}`,
        category: 'needs-attention',
        severity: 'medium',
        source: 'show',
        title: `Venue Advance ${i}`,
        description: `Show on ${new Date(2026, 1, i).toLocaleDateString()} in ${i + 10} days`,
        link: '/shows',
        itemId: `mock-show-advance-${i}`,
        actionType: 'missing-advance-info'
      })
    }
    
    for (let i = 1; i <= 33; i++) {
      actions.push({
        id: `mock-missing-schedule-${i}`,
        category: 'needs-attention',
        severity: 'medium',
        source: 'show',
        title: `Venue Schedule ${i}`,
        description: `Show on ${new Date(2026, 1, i).toLocaleDateString()} in ${i + 5} days`,
        link: '/shows',
        itemId: `mock-show-schedule-${i}`,
        actionType: 'missing-schedule'
      })
    }
    
    for (let i = 1; i <= 38; i++) {
      actions.push({
        id: `mock-missing-guarantee-${i}`,
        category: 'needs-attention',
        severity: 'critical',
        source: 'show',
        title: `Venue Guarantee ${i}`,
        description: `Show on ${new Date(2026, 1, i + 15).toLocaleDateString()}`,
        link: '/shows',
        itemId: `mock-show-guarantee-${i}`,
        actionType: 'missing-guarantee'
      })
    }
    
    for (let i = 1; i <= 38; i++) {
      actions.push({
        id: `mock-missing-promoter-${i}`,
        category: 'needs-attention',
        severity: 'critical',
        source: 'show',
        title: `Venue Contact ${i}`,
        description: `Show on ${new Date(2026, 2, i).toLocaleDateString()}`,
        link: '/shows',
        itemId: `mock-show-contact-${i}`,
        actionType: 'missing-promoter-contact'
      })
    }
    
    actions.push({
      id: 'mock-needs-5',
      category: 'needs-attention',
      severity: 'critical',
      source: 'music',
      title: 'Electric Dreams',
      description: 'Releasing 2/15/2026 in 20 days',
      link: '/music',
      itemId: 'mock-song-2',
      actionType: 'missing-master-splits'
    })
    
    actions.push({
      id: 'mock-needs-6',
      category: 'needs-attention',
      severity: 'critical',
      source: 'music',
      title: 'Midnight Drive',
      description: 'Releasing 2/28/2026 in 33 days',
      link: '/music',
      itemId: 'mock-song-3',
      actionType: 'missing-publishing-splits'
    })
    } // End of USE_MOCK_DATA

    return actions
  }

  const groupActions = (actions: ActionItem[]): ActionGroup[] => {
    const groups: Record<string, ActionGroup> = {}

    const actionTypeLabels: Record<string, string> = {
      'missing-guarantee': 'Missing guarantee',
      'missing-split-point': 'Missing split point',
      'missing-promoter-contact': 'Missing promoter contact',
      'missing-capacity': 'Missing capacity',
      'overdue-deposit': 'Overdue deposits',
      'missing-show-time': 'Missing show time',
      'missing-ticket-prices': 'Missing ticket prices',
      'missing-advance-info': 'Missing advance info',
      'missing-schedule': 'Missing schedule',
      'show-tomorrow': 'Shows tomorrow',
      'low-ticket-sales': 'Low ticket sales',
      'red-flag': 'Red flagged shows',
      'overdue-deposit-risk': 'Overdue deposits',
      'below-target-sales': 'Below target sales',
      'missing-master-splits': 'Missing master splits',
      'missing-publishing-splits': 'Missing publishing splits',
      'no-pro-registration': 'No PRO registration'
    }

    actions.forEach((action) => {
      if (action.category === 'task') return // Tasks handled separately
      
      const key = `${action.category}-${action.actionType}`
      
      if (!groups[key]) {
        groups[key] = {
          type: action.actionType || 'unknown',
          category: action.category,
          label: actionTypeLabels[action.actionType || ''] || action.actionType || 'Unknown',
          count: 0,
          items: []
        }
      }
      
      groups[key].count++
      groups[key].items.push(action)
    })

    return Object.values(groups)
  }

  const compileSchedule = (
    shows: Show[],
    tasks: Task[],
    songs: Song[],
    calendarEvents: CalendarEvent[]
  ) => {
    const today = getToday()
    const tomorrow = getTomorrow()
    const todayItems: ScheduleItem[] = []
    const tomorrowItems: ScheduleItem[] = []

    // Add shows
    shows.forEach((show) => {
      const showDate = show.date || show.perf_date || ''
      if (showDate === today) {
        todayItems.push({
          id: `show-${show.id}`,
          type: 'show',
          title: show.event_title || show.title || show.venue,
          time: show.show_time,
          location: `${show.venue} • ${show.city}`,
          date: showDate,
          details: show.tix_sold ? `${show.tix_sold} tickets` : undefined
        })
      } else if (showDate === tomorrow) {
        tomorrowItems.push({
          id: `show-${show.id}`,
          type: 'show',
          title: show.event_title || show.title || show.venue,
          time: show.show_time,
          location: `${show.venue} • ${show.city}`,
          date: showDate,
          details: show.tix_sold ? `${show.tix_sold} tickets` : undefined
        })
      }
    })

    // Add tasks with due dates
    tasks.forEach((task) => {
      if (!task.due_date || task.status !== 'pending') return
      const taskDate = toLocalDateKey(parseLocalDate(task.due_date) || new Date(0))
      if (taskDate === today) {
        todayItems.push({
          id: `task-${task.id}`,
          type: 'task',
          title: task.title,
          date: taskDate,
          details: task.priority ? `${task.priority} priority` : undefined
        })
      } else if (taskDate === tomorrow) {
        tomorrowItems.push({
          id: `task-${task.id}`,
          type: 'task',
          title: task.title,
          date: taskDate,
          details: task.priority ? `${task.priority} priority` : undefined
        })
      }
    })

    // Add song releases
    songs.forEach((song) => {
      if (!song.release_date) return
      const releaseDate = song.release_date
      if (releaseDate === today) {
        todayItems.push({
          id: `release-${song.id}`,
          type: 'release',
          title: `Release: ${song.song_title || song.title || 'Untitled'}`,
          date: releaseDate
        })
      } else if (releaseDate === tomorrow) {
        tomorrowItems.push({
          id: `release-${song.id}`,
          type: 'release',
          title: `Release: ${song.song_title || song.title || 'Untitled'}`,
          date: releaseDate
        })
      }
    })

    // Add calendar events
    calendarEvents.forEach((event) => {
      const eventDate = event.start.split('T')[0]
      if (eventDate === today) {
        todayItems.push({
          id: `calendar-${event.id}`,
          type: 'calendar',
          title: event.title,
          location: event.location,
          date: eventDate,
          time: new Date(event.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        })
      } else if (eventDate === tomorrow) {
        tomorrowItems.push({
          id: `calendar-${event.id}`,
          type: 'calendar',
          title: event.title,
          location: event.location,
          date: eventDate,
          time: new Date(event.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        })
      }
    })

    // Sort by time if available
    const sortByTime = (a: ScheduleItem, b: ScheduleItem) => {
      if (a.time && b.time) {
        return a.time.localeCompare(b.time)
      }
      if (a.time) return -1
      if (b.time) return 1
      return 0
    }

    // No mock fallbacks: schedule is either real data or empty-state.
    setScheduleToday(todayItems.sort(sortByTime))
    setScheduleTomorrow(tomorrowItems.sort(sortByTime))
  }

  const applyData = (
    showsData: any,
    songsData: any,
    ordersData: any,
    tasksData: any,
    financialsData: any,
    calendarData: any
  ) => {
    const shows = normalizeBookings(showsData)
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const upcoming = shows
      .filter((s: Show) => {
        const showDate = parseLocalDate(s.date || s.perf_date || '')
        return showDate && showDate >= now && (s.status === 'upcoming' || s.status === 'confirmed' || s.status === 'announced')
      })
      .sort((a: Show, b: Show) => (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) - (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0))
      .slice(0, 3)

    const past = shows
      .filter((s: Show) => {
        const showDate = parseLocalDate(s.date || s.perf_date || '')
        return showDate && showDate < now
      })
      .sort((a: Show, b: Show) => (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0) - (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0))
      .slice(0, 5)

    const tasks = normalizeList<Task>(extractArray(tasksData) as Task[])
    const catalogSongs = normalizeCatalog(songsData)

    const events = calendarData?.data?.data?.events || []
    const normalizedEvents: CalendarEvent[] = Array.isArray(events)
      ? events.map((ev: any) => ({
          id: ev.id || `cal-${Math.random()}`,
          title: ev.summary || '(No title)',
          start: ev.start?.dateTime || ev.start?.date || '',
          location: ev.location || '',
          description: ev.description || '',
        }))
      : []

    setUpcomingShows(upcoming)
    setPastShows(past)
    setAllShows(shows)
    setSongs(catalogSongs)
    setOrders(normalizeList<Order>(extractArray(ordersData) as Order[]))
    setFinancialEntries(normalizeList<FinancialEntry>(extractArray(financialsData) as FinancialEntry[]))

    // Compile schedule + actions
    compileSchedule(shows, tasks, catalogSongs, normalizedEvents)
    const actions = evaluateActions(shows, catalogSongs, tasks)
    setActionItems(actions)
    setActionGroups(groupActions(actions))
  }

  // Hydrate from session cache before first paint, then refresh in background
  useLayoutEffect(() => {
    try {
      const readApi = (endpoint: string) => readSessionCache<any>(makeCacheKey([userId, 'api', endpoint]))
      const cachedShows = readApi('user/getBookings')
      const cachedSongs = readApi('user/getCatalog')
      const cachedOrders = readApi('orders')
      const cachedTasks = readApi('tasks')
      const cachedFinancials = readApi('financials')
      const cachedCalendar =
        readApi('user/connectors/googleCalendar/events?maxResults=250') ??
        readApi('user/connectors/googleCalendar/events?maxResults=50')

      if (cachedShows || cachedSongs || cachedOrders || cachedTasks || cachedFinancials || cachedCalendar) {
        applyData(
          cachedShows || [],
          cachedSongs || [],
          cachedOrders || [],
          cachedTasks || [],
          cachedFinancials || [],
          cachedCalendar || { data: { data: { events: [] } } }
        )
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [showsData, songsData, ordersData, tasksData, _usersData, financialsData, calendarData] = await Promise.all([
          getDataAPI('user/getBookings').catch(() => []),
          getDataAPI('user/getCatalog').catch(() => []),
          getDataAPI('orders').catch(() => []),
          getDataAPI('tasks').catch(() => []),
          getDataAPI('users').catch(() => []),
          getDataAPI('financials').catch(() => []),
          getDataAPI('user/connectors/googleCalendar/events?maxResults=50').catch(() => ({ data: { data: { events: [] } } })),
        ])
        // Cache raw responses for instant navigation next time
        writeSessionCache(makeCacheKey([userId, 'api', 'user/getBookings']), showsData)
        writeSessionCache(makeCacheKey([userId, 'api', 'user/getCatalog']), songsData)
        writeSessionCache(makeCacheKey([userId, 'api', 'orders']), ordersData)
        writeSessionCache(makeCacheKey([userId, 'api', 'tasks']), tasksData)
        writeSessionCache(makeCacheKey([userId, 'api', 'financials']), financialsData)
        writeSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']), calendarData)

        applyData(showsData, songsData, ordersData, tasksData, financialsData, calendarData)
      } catch (error) {
        console.error('Error fetching overview data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const today = getToday()
  const weekStart = getWeekStart()
  const monthStart = getMonthStart()
  const endDate = todayKey()

  const getResolvedTicketCount = (show: Show) => {
    const val = (show.tix_sold ?? show.ticket_count ?? 0) as any
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isNaN(n) ? 0 : Math.max(0, n)
  }

  // Rolling last 7 days (inclusive of today) based on *when ticket counts were entered*,
  // matching Shows → "Ticket counts / Last 7 days".
  const todayLocal = new Date()
  todayLocal.setHours(0, 0, 0, 0)
  const last7Start = new Date(todayLocal)
  last7Start.setDate(todayLocal.getDate() - 6)
  last7Start.setHours(0, 0, 0, 0)
  const last7End = new Date(todayLocal)
  last7End.setHours(23, 59, 59, 999)

  const showsWithTicketUpdatesLast7Days = allShows.filter((show) => {
    const raw = (show as any).raw ?? show
    const ts =
      raw.last_ticket_count_date ??
      raw.lastTicketCountDate ??
      raw.last_ticket_count_at ??
      raw.lastTicketCountAt ??
      raw.updated_at ??
      raw.updatedAt
    if (!ts) return false
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return false
    return d.getTime() >= last7Start.getTime() && d.getTime() <= last7End.getTime()
  })

  const ticketsWeek = showsWithTicketUpdatesLast7Days.reduce((sum, s) => sum + getResolvedTicketCount(s), 0)

  // Calculate ticket metrics for new Tickets section
  // Open Tickets: same as Shows → "Unsold Tickets"
  // Sum across shows of max(0, capacity - ticketsSold).
  const getCapacity = (show: Show) => {
    const raw = (show as any).raw ?? {}
    const val =
      show.sellable_cap ??
      raw.sellable_cap ??
      raw.capacity ??
      show.capacity ??
      raw.cap ??
      raw.venue_capacity

    if (val === undefined || val === null || val === '') return 0
    const n =
      typeof val === 'number'
        ? val
        : Number(String(val).replace(/[^0-9.-]/g, ''))
    return Number.isNaN(n) ? 0 : Math.max(0, n)
  }

  const openTickets = allShows.reduce((sum, show) => {
    const cap = getCapacity(show)
    if (cap <= 0) return sum
    const sold = getResolvedTicketCount(show)
    return sum + Math.max(0, cap - sold)
  }, 0)

  // Sold Out Shows YTD: Shows this year where tickets sold >= capacity
  const yearStart = toLocalDateKey(new Date(new Date().getFullYear(), 0, 1))
  const soldOutShowsYTD = allShows.filter(s => {
    const showDate = s.date || s.perf_date || ''
    if (showDate < yearStart) return false
    
    const ticketsSold = s.tix_sold || s.ticket_count || 0
    const capacity = Number(s.sellable_cap) || s.capacity || 0
    
    return capacity > 0 && ticketsSold >= capacity
  }).length

  // const streamsTotal = songs.reduce((sum, s) => sum + (s.streams_manual_override ?? s.streams_total ?? 0), 0)
  // const streamsToday = songs.reduce((sum, s) => sum + (s.streams_today || 0), 0)

  // Find next upcoming release
  const nowForSongs = new Date()
  nowForSongs.setHours(0, 0, 0, 0)
  const upcomingSongs = songs
    .filter(s => s.release_date && (parseLocalDate(s.release_date)?.getTime() ?? 0) > nowForSongs.getTime())
    .sort((a, b) => (parseLocalDate(a.release_date!)?.getTime() ?? 0) - (parseLocalDate(b.release_date!)?.getTime() ?? 0))
  const nextRelease = upcomingSongs[0]
  const nextReleaseDays = nextRelease 
    ? Math.ceil(((parseLocalDate(nextRelease.release_date!) || nowForSongs).getTime() - nowForSongs.getTime()) / (1000 * 60 * 60 * 24))
    : null

  // Find latest released song
  const releasedSongs = songs
    .filter(s => s.release_date && (parseLocalDate(s.release_date)?.getTime() ?? 0) <= nowForSongs.getTime())
    .sort((a, b) => (parseLocalDate(b.release_date!)?.getTime() ?? 0) - (parseLocalDate(a.release_date!)?.getTime() ?? 0))
  const latestRelease = releasedSongs[0]

  const ordersToday = orders.filter(o => o.order_date === today)
  const ordersWeek = orders.filter(o => {
    const orderDate = o.order_date
    return orderDate >= weekStart && orderDate <= endDate
  })
  const ordersMonth = orders.filter(o => {
    const orderDate = o.order_date
    return orderDate >= monthStart && orderDate <= endDate
  })

  const revenueCommerceToday = ordersToday.reduce((sum, o) => sum + o.total, 0)
  const revenueCommerceWeek = ordersWeek.reduce((sum, o) => sum + o.total, 0)
  const revenueCommerceMonth = ordersMonth.reduce((sum, o) => sum + o.total, 0)

  // Get top 5 offers expiring next (status = 'pending' and has offer_expires date)
  const realOffers = allShows
    .filter(s => s.status === 'pending' && s.offer_expires && (parseLocalDate(s.offer_expires)?.getTime() ?? 0) >= nowForSongs.getTime())
    .sort((a, b) => (parseLocalDate(a.offer_expires!)?.getTime() ?? 0) - (parseLocalDate(b.offer_expires!)?.getTime() ?? 0))
    .slice(0, 5)
  
  // Use mock data if no real offers exist
  const offersExpiringNext = realOffers.length > 0 ? realOffers : MOCK_OFFERS

  // const financialToday = financialEntries.filter(f => f.date >= today && f.date <= endDate)
  // const financialWeek = financialEntries.filter(f => f.date >= weekStart && f.date <= endDate)
  // const financialMonth = financialEntries.filter(f => f.date >= monthStart && f.date <= endDate)

  // const revenueToday = financialToday.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  // const spendToday = financialToday.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)
  // const revenueWeek = financialWeek.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  // const spendWeek = financialWeek.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)
  // const revenueMonth = financialMonth.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  // const spendMonth = financialMonth.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)

  // Get recent financial entries for table display (sorted by date, most recent first)
  const realFinancialEntries = financialEntries
    .sort((a, b) => (parseLocalDate(b.date)?.getTime() ?? 0) - (parseLocalDate(a.date)?.getTime() ?? 0))
    .slice(0, 10)
  
  // Use mock data if no real financial entries exist
  const recentFinancialEntries = realFinancialEntries.length > 0 ? realFinancialEntries : MOCK_FINANCIAL_ENTRIES

  // Hero: one big highlight = next thing by priority (schedule → task → show → release), today then tomorrow
  const releaseTitle = (title: string) => title.replace(/^Release: /, '')
  const typePriority = (t: ScheduleItem['type']) =>
    t === 'calendar' ? 0 : t === 'task' ? 1 : t === 'show' ? 2 : t === 'release' ? 3 : 2 // flight/hotel/transport like show
  const byPriorityThenTime = (a: ScheduleItem, b: ScheduleItem) => {
    const pa = typePriority(a.type)
    const pb = typePriority(b.type)
    if (pa !== pb) return pa - pb
    if (a.time && b.time) return a.time.localeCompare(b.time)
    if (a.time) return -1
    if (b.time) return 1
    return 0
  }
  const sortedToday = [...scheduleToday].sort(byPriorityThenTime)
  const sortedTomorrow = [...scheduleTomorrow].sort(byPriorityThenTime)
  const featureItem = sortedToday[0] || sortedTomorrow[0]
  const isFeatureToday = sortedToday.length > 0

  const heroMainLine = !featureItem
    ? 'All clear for today'
    : isFeatureToday && featureItem.type === 'release'
      ? `New release: ${releaseTitle(featureItem.title)}`
      : isFeatureToday && featureItem.type === 'show'
        ? `Today: ${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''}`
        : isFeatureToday && featureItem.type === 'task'
          ? `${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''}`
          : isFeatureToday
            ? `${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''}`
            : featureItem.type === 'release'
              ? `${releaseTitle(featureItem.title)}${featureItem.time ? ` at ${featureItem.time}` : ''}`
              : featureItem.time
                ? `${featureItem.title} at ${featureItem.time}`
                : featureItem.title

  // Next task for Tasks card: first task today, else first task tomorrow, else first from action items
  const tasksToday = scheduleToday.filter((i) => i.type === 'task')
  const tasksTomorrow = scheduleTomorrow.filter((i) => i.type === 'task')
  const nextTaskFromSchedule = tasksToday[0] || tasksTomorrow[0]
  const nextTaskFromActions = actionItems.find((a) => a.category === 'task')
  const nextTaskDisplay = nextTaskFromSchedule
    ? nextTaskFromSchedule.title + (nextTaskFromSchedule.time ? ` at ${nextTaskFromSchedule.time}` : '')
    : nextTaskFromActions
      ? nextTaskFromActions.title
      : 'Nothing due'
  const nextTaskSubtext = nextTaskFromSchedule
    ? (nextTaskFromSchedule.date === today ? 'Today' : localDateDisplay(nextTaskFromSchedule.date, { weekday: 'short' }))
    : nextTaskFromActions?.dueDate
      ? (toLocalDateKey(parseLocalDate(nextTaskFromActions.dueDate) || new Date()) === today ? 'Due today' : `Due ${shortDateDisplay(nextTaskFromActions.dueDate)}`)
      : ''

  const latestReleaseIsToday =
    releasedSongs.length > 0 &&
    releasedSongs[0].release_date &&
    toLocalDateKey(parseLocalDate(releasedSongs[0].release_date) || new Date(0)) === today
  const latestReleaseDisplay =
    releasedSongs.length > 0
      ? latestReleaseIsToday
        ? `New release: ${releasedSongs[0].song_title || releasedSongs[0].title || 'Release'}`
        : releasedSongs[0].song_title || releasedSongs[0].title || 'Release'
      : 'No recent'
  const latestReleaseSubtext =
    releasedSongs.length > 0
      ? latestReleaseIsToday
        ? 'Out today'
        : releasedSongs[0].streams_total != null
          ? `${Number(releasedSongs[0].streams_total).toLocaleString()} streams`
          : ''
      : ''

  const nextShow = upcomingShows.length > 0 ? upcomingShows[0] : null
  const nextShowIsToday =
    nextShow &&
    (nextShow.date || nextShow.perf_date) &&
    toLocalDateKey(parseLocalDate(nextShow.date || nextShow.perf_date!) || new Date(0)) === today
  const nextShowDisplay =
    nextShow ?
      nextShowIsToday
        ? `Today: ${nextShow.venue || nextShow.title || 'Show'}`
        : nextShow.venue || nextShow.title || 'Show'
      : 'None'
  const nextShowSubtext =
    nextShow && nextShow.date
      ? nextShowIsToday
        ? 'Today'
        : shortDateDisplay(nextShow.date)
      : ''

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 sm:mb-10 flex items-start justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">Overview</h1>
          <p className="text-xs sm:text-sm text-gray-500">Single source of truth for your operation</p>
        </div>
        
        {/* Layout Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsLayoutDropdownOpen(!isLayoutDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all shadow-sm hover:shadow"
          >
            Layout
            <svg 
              className={`w-3 h-3 transition-transform duration-200 ${isLayoutDropdownOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {isLayoutDropdownOpen && (
            <>
              {/* Backdrop for mobile */}
              <div 
                className="fixed inset-0 z-40 sm:hidden" 
                onClick={() => setIsLayoutDropdownOpen(false)}
              />
              
              <div className="absolute right-0 mt-3 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Header */}
                <div className="bg-white dark:bg-gray-800 px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Customize Layout</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Drag to reorder, toggle to show/hide</p>
                    </div>
                    <button
                      onClick={() => setIsLayoutDropdownOpen(false)}
                      className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Sections List */}
                <div className="p-3 max-h-[450px] overflow-y-auto custom-scrollbar">
                  <div className="space-y-1.5">
                    {sections.map((section, index) => (
                      <div
                        key={section.id}
                        draggable
                        onDragStart={() => setDraggedIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedIndex !== null && draggedIndex !== index) {
                            moveSection(draggedIndex, index)
                          }
                          setDraggedIndex(null)
                        }}
                        onDragEnd={() => setDraggedIndex(null)}
                        className={`group flex items-center gap-3 p-3 rounded-lg border cursor-move transition-all ${
                          draggedIndex === index 
                            ? 'border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-700 shadow-md scale-105' 
                            : section.enabled
                              ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
                              : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 opacity-60'
                        }`}
                      >
                        {/* Drag Handle */}
                        <div className="flex-shrink-0">
                          <svg 
                            className={`w-4 h-4 transition-colors ${
                              section.enabled ? 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400' : 'text-gray-300 dark:text-gray-600'
                            }`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                          </svg>
                        </div>
                        
                        {/* Label */}
                        <span className={`text-sm flex-1 transition-colors ${
                          section.enabled ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-500'
                        }`}>
                          {section.label}
                        </span>
                        
                        {/* Checkbox - compact sharp design with centered checkmark */}
                        <div className="flex-shrink-0 relative flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={section.enabled}
                            onChange={(e) => {
                              e.stopPropagation()
                              toggleSection(section.id)
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out appearance-none cursor-pointer focus:ring-2 focus:ring-offset-0 ${
                              section.enabled
                                ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-gray-900 dark:focus:ring-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          />
                          {section.enabled && (
                            <svg 
                              className="absolute w-3 h-3 pointer-events-none dark:hidden checkbox-check" 
                              viewBox="0 0 20 20" 
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path 
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                fill="white"
                              />
                            </svg>
                          )}
                          {section.enabled && (
                            <svg 
                              className="absolute w-3 h-3 pointer-events-none hidden dark:block checkbox-check" 
                              viewBox="0 0 20 20" 
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path 
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                fill="#111111"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Footer */}
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={resetLayout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-750 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset to Default
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Main content wrapper with flex display for ordering */}
      <div className="flex flex-col">
      
      {/* Custom scrollbar styles - add to document head or global CSS */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 3px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        @keyframes slide-in-from-top-2 {
          from {
            transform: translateY(-8px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-in {
          animation: slide-in-from-top-2 0.2s ease-out;
        }
        .animate-in.fade-in {
          animation: fade-in 0.15s ease-out;
        }
        .checkbox-check {
          animation: fade-in 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>

      {/* Hero: same style as other Overview modules (white/gray card, gray text) */}
      {renderSection('hero',
      <section className="mb-6 sm:mb-10">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-stretch">
            {/* Left: greeting + main stat */}
            <div className="flex-shrink-0 p-6 sm:p-8 lg:max-w-md">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 tracking-wide">
                Yo, {getOverviewUserName()}!
              </p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-tight mb-2">
                {heroMainLine}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your day at a glance.
              </p>
            </div>

            {/* Right: 4 cards only — Schedule, Tasks, Next Show, Latest Release */}
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 p-6 sm:p-8 lg:pl-8">
              {/* Schedule — what's on your schedule */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Schedule</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {scheduleToday.length + scheduleTomorrow.length > 0 ? `${scheduleToday.length + scheduleTomorrow.length} events` : 'No events'}
                </p>
                {scheduleToday.length + scheduleTomorrow.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Today & tomorrow</p>
                )}
              </div>
              {/* Tasks — your next task */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Tasks</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {nextTaskDisplay}
                </p>
                {nextTaskSubtext && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{nextTaskSubtext}</p>
                )}
              </div>
              {/* Next Show */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  {nextShowIsToday ? 'Today' : 'Next Show'}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {nextShowDisplay}
                </p>
                {nextShowSubtext && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{nextShowSubtext}</p>
                )}
              </div>
              {/* Latest Release — one card only; "New release" when release date is today */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Latest Release</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {latestReleaseDisplay}
                </p>
                {latestReleaseSubtext && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{latestReleaseSubtext}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Notes */}
      {renderSection('notes',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Notes</h2>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full min-h-[150px] sm:min-h-[200px] p-3 sm:p-4 border border-gray-200 rounded-lg resize-y text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
          placeholder="Enter notes here..."
        />
      </section>
      )}

      {/* Schedule */}
      {renderSection('schedule',
      <section className="mb-6 sm:mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-900">Schedule</h2>
          <Link to="/shows" className="text-xs text-gray-500 hover:text-gray-900">
            View all →
          </Link>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          {/* Today Section */}
          <div className="mb-6 sm:mb-8">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">TODAY</h3>
            <div className="space-y-3 sm:space-y-4">{scheduleToday.length > 0 ? (
                scheduleToday.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 py-1">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: 
                          item.type === 'show' ? '#9333ea' :
                          item.type === 'task' ? '#3b82f6' :
                          item.type === 'release' ? '#06b6d4' :
                          item.type === 'flight' ? '#f97316' :
                          item.type === 'hotel' ? '#f97316' :
                          item.type === 'transport' ? '#f97316' :
                          '#475569'
                      }}></div>
                    </div>
                    <div className="flex items-start justify-between flex-1">
                      <div className="flex-1">
                        {item.time && (
                          <p className="text-xs text-gray-500 mb-1">{item.time}</p>
                        )}
                        <h4 className="text-sm font-medium text-gray-900 mb-1">{item.title}</h4>
                        {item.location && (
                          <p className="text-xs text-gray-600 mb-1">{item.location}</p>
                        )}
                        {item.details && (
                          <p className="text-xs text-gray-500">{item.details}</p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        item.type === 'show' ? 'bg-purple-100 text-purple-800' :
                        item.type === 'task' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'release' ? 'bg-cyan-100 text-cyan-800' :
                        item.type === 'flight' ? 'bg-orange-100 text-orange-800' :
                        item.type === 'hotel' ? 'bg-orange-100 text-orange-800' :
                        item.type === 'transport' ? 'bg-orange-100 text-orange-800' :
                        'bg-slate-200 text-slate-800'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No schedule items for today</p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Tomorrow Section */}
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">TOMORROW</h3>
            <div className="space-y-4">
              {scheduleTomorrow.length > 0 ? (
                scheduleTomorrow.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 py-1">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: 
                          item.type === 'show' ? '#9333ea' :
                          item.type === 'task' ? '#3b82f6' :
                          item.type === 'release' ? '#06b6d4' :
                          item.type === 'flight' ? '#f97316' :
                          item.type === 'hotel' ? '#f97316' :
                          item.type === 'transport' ? '#f97316' :
                          '#475569'
                      }}></div>
                    </div>
                    <div className="flex items-start justify-between flex-1">
                      <div className="flex-1">
                        {item.time && (
                          <p className="text-xs text-gray-500 mb-1">{item.time}</p>
                        )}
                        <h4 className="text-sm font-medium text-gray-900 mb-1">{item.title}</h4>
                        {item.location && (
                          <p className="text-xs text-gray-600 mb-1">{item.location}</p>
                        )}
                        {item.details && (
                          <p className="text-xs text-gray-500">{item.details}</p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        item.type === 'show' ? 'bg-purple-100 text-purple-800' :
                        item.type === 'task' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'release' ? 'bg-cyan-100 text-cyan-800' :
                        item.type === 'flight' ? 'bg-orange-100 text-orange-800' :
                        item.type === 'hotel' ? 'bg-orange-100 text-orange-800' :
                        item.type === 'transport' ? 'bg-orange-100 text-orange-800' :
                        'bg-slate-200 text-slate-800'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No schedule items for tomorrow</p>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Actions - Tasks and Items Needing Attention */}
      {renderSection('actions',
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-sm font-medium text-gray-900">Actions</h2>
          <button
            onClick={() => setIsInfoModalOpen(true)}
            className="w-3.5 h-3.5 rounded-full border border-gray-400 hover:border-gray-600 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
            style={{ fontSize: '8px' }}
            aria-label="About Actions"
          >
            i
          </button>
        </div>

        {actionItems.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {/* Tasks */}
            {actionItems.filter(a => a.category === 'task').length > 0 && (
              <div className={actionGroups.filter(g => g.category === 'at-risk' || g.category === 'needs-attention').length > 0 ? 'mb-8' : ''}>
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">TASKS</h3>
                <div className="space-y-3">
                  {actionItems
                    .filter(a => a.category === 'task')
                    .slice(0, 5)
                    .map((action) => {
                      // Determine color based on what the task is related to
                      const getTaskColor = () => {
                        if (action.relatedTo === 'shows') return '#9333ea' // Purple (Shows)
                        if (action.relatedTo === 'music') return '#06b6d4' // Cyan (Music)
                        if (action.relatedTo === 'commerce') return '#10b981' // Green (Commerce)
                        if (action.relatedTo === 'finance') return '#10b981' // Green (Finance)
                        if (action.relatedTo === 'tour-manager') return '#f97316' // Orange (Tour Manager)
                        return '#3b82f6' // Blue (General tasks)
                      }
                      
                      return (
                        <Link
                          key={action.id}
                          to={action.link || '#'}
                          className="flex items-start gap-3 py-2 hover:bg-gray-50/50 transition-colors rounded"
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getTaskColor() }}></div>
                          </div>
                          <div className="flex items-start justify-between flex-1">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">{action.title}</h4>
                              <p className="text-xs text-gray-600">{action.description}</p>
                            </div>
                            {action.severity === 'critical' && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium text-red-700 bg-red-50 rounded flex-shrink-0">
                                Overdue
                              </span>
                            )}
                          </div>
                        </Link>
                      )
                    })}
                </div>
              </div>
            )}

            {/* Divider */}
            {actionItems.filter(a => a.category === 'task').length > 0 && 
             actionGroups.filter(g => g.category === 'at-risk' || g.category === 'needs-attention').length > 0 && (
              <div className="border-t border-gray-200 my-6"></div>
            )}

            {/* At Risk & Needs Attention Combined - Grouped */}
            {(actionGroups.filter(g => g.category === 'at-risk').length > 0 || 
              actionGroups.filter(g => g.category === 'needs-attention').length > 0) && (
              <div>
                {/* At Risk Section */}
                {actionGroups.filter(g => g.category === 'at-risk').length > 0 && (
                  <div className={actionGroups.filter(g => g.category === 'needs-attention').length > 0 ? 'mb-8' : ''}>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">AT RISK</h3>
                    <div className="space-y-3">
                      {actionGroups
                        .filter(g => g.category === 'at-risk')
                        .map((group) => {
                          // Determine color based on source
                          const dotColor = group.items[0]?.source === 'music' ? '#06b6d4' : '#9333ea'
                          return (
                            <button
                              key={group.type}
                              onClick={() => setSelectedActionGroup(group)}
                              className="w-full text-left hover:bg-gray-50/50 transition-colors py-2 rounded flex items-center gap-3"
                            >
                              <div className="flex-shrink-0">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }}></div>
                              </div>
                              <div className="flex items-center justify-between flex-1">
                                <span className="text-sm font-medium text-gray-900">{group.label}</span>
                                <span className="text-sm text-gray-600">{group.count}</span>
                              </div>
                            </button>
                          )
                        })}
                    </div>
                  </div>
                )}

                {/* Divider */}
                {actionGroups.filter(g => g.category === 'at-risk').length > 0 && 
                 actionGroups.filter(g => g.category === 'needs-attention').length > 0 && (
                  <div className="border-t border-gray-200 my-6"></div>
                )}

                {/* Needs Attention Section */}
                {actionGroups.filter(g => g.category === 'needs-attention').length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4">NEEDS ATTENTION</h3>
                    <div className="space-y-3">
                      {actionGroups
                        .filter(g => g.category === 'needs-attention')
                        .map((group) => {
                          // Determine color based on source
                          const dotColor = group.items[0]?.source === 'music' ? '#06b6d4' : '#9333ea'
                          return (
                            <button
                              key={group.type}
                              onClick={() => setSelectedActionGroup(group)}
                              className="w-full text-left hover:bg-gray-50/50 transition-colors py-2 rounded flex items-center gap-3"
                            >
                              <div className="flex-shrink-0">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }}></div>
                              </div>
                              <div className="flex items-center justify-between flex-1">
                                <span className="text-sm font-medium text-gray-900">{group.label}</span>
                                <span className="text-sm text-gray-600">{group.count}</span>
                              </div>
                            </button>
                          )
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-sm text-gray-500">No actions requiring attention</p>
            <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
          </div>
        )}
      </section>
      )}

      {/* Action Details Modal */}
      {selectedActionGroup && (
        <Modal 
          isOpen={!!selectedActionGroup} 
          onClose={() => setSelectedActionGroup(null)} 
          title={selectedActionGroup.label}
        >
          <div className="space-y-3">
            {selectedActionGroup.items.map((item) => {
              // Determine color based on source and relatedTo
              const getDotColor = () => {
                if (item.source === 'task') {
                  // For tasks, use relatedTo field to determine color
                  if (item.relatedTo === 'shows') return '#9333ea' // Purple
                  if (item.relatedTo === 'music') return '#06b6d4' // Cyan
                  if (item.relatedTo === 'commerce') return '#10b981' // Green
                  if (item.relatedTo === 'finance') return '#10b981' // Green
                  if (item.relatedTo === 'tour-manager') return '#f97316' // Orange
                  return '#3b82f6' // Blue (General)
                }
                // For shows and music, use source
                return item.source === 'music' ? '#06b6d4' : '#9333ea'
              }
              
              return (
                <Link
                  key={item.id}
                  to={item.link || '#'}
                  state={{ editItemId: item.itemId, source: item.source }}
                  onClick={() => setSelectedActionGroup(null)}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getDotColor() }}></div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-xs text-gray-600">{item.description}</p>
                  </div>
                </Link>
              )
            })}
          </div>
        </Modal>
      )}

      {/* Info Modal */}
      <Modal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} title="What are Actions?">
        <div className="space-y-8 text-sm">
          <p className="text-gray-700">
            Actions is your command center for staying on top of critical items that need immediate attention.
          </p>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-base">Tasks</h4>
            <p className="text-gray-600 text-sm">
              Due today or overdue tasks from your task list that require completion.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-base">At Risk</h4>
            <p className="text-gray-600 text-sm mb-2">
              Time-sensitive issues that could impact upcoming events:
            </p>
            <div className="ml-4 space-y-1 text-xs text-gray-600">
              <p><strong>Shows:</strong></p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Missing travel bookings for imminent shows</li>
                <li>Low ticket sales approaching show date</li>
                <li>Overdue deposits or contracts</li>
                <li>Missing critical show information</li>
              </ul>
              <p className="mt-2"><strong>Music:</strong></p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Releases approaching without distributor upload</li>
                <li>Missing Spotify pitch submissions</li>
                <li>Upcoming releases without PRO registration</li>
                <li>Release day without planned announcement</li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2 text-base">Needs Attention</h4>
            <p className="text-gray-600 text-sm mb-2">
              Critical information missing that's needed for success:
            </p>
            <div className="ml-4 space-y-1 text-xs text-gray-600">
              <p><strong>Shows:</strong></p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Financial details (guarantees, splits, deposits)</li>
                <li>Contact information (promoters, venues)</li>
                <li>Operational details (show times, advance info)</li>
              </ul>
              <p className="mt-2"><strong>Music:</strong></p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Master ownership splits not documented</li>
                <li>Publishing splits & PRO information missing</li>
                <li className="italic">(Only for manually added upcoming releases)</li>
              </ul>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 italic">
              TIP: Most release metadata (ISRC, artwork, streaming links) is automatically imported on release day for songs you've added with Artist - Title format.
            </p>
          </div>

          <div className="mt-4">
            <p className="text-xs text-gray-600">
              Click any item to view details and take action.
            </p>
          </div>
        </div>
      </Modal>


      {/* Upcoming Shows */}
      {renderSection('upcoming-shows',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Upcoming Shows</h2>
        {upcomingShows.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="hidden sm:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Guarantee</th>
                  <th className="text-right py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Tickets</th>
                  <th className="hidden lg:table-cell text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Capacity</th>
                  <th className="hidden sm:table-cell text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">% Sold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {upcomingShows.map((show) => {
                  const ticketsSold = show.tix_sold || show.ticket_count || 0
                  const capacity = Number(show.sellable_cap) || show.capacity || 0
                  const percentSold = capacity > 0 ? Math.round((ticketsSold / capacity) * 100) : 0
                  
                  return (
                    <tr key={show.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">{shortDateDisplay(show.date || show.perf_date)}</td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm text-gray-600">{show.city}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-900">{show.venue}</td>
                      <td className="hidden md:table-cell py-3 px-4 text-sm text-gray-900">{show.guarantee || '-'}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-right text-gray-900 font-medium">{ticketsSold.toLocaleString()}</td>
                      <td className="hidden lg:table-cell py-3 px-4 text-sm text-right text-gray-600">{capacity > 0 ? capacity.toLocaleString() : '-'}</td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm text-right text-gray-900 font-medium">
                        {capacity > 0 ? `${percentSold}%` : '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-sm text-gray-500">No upcoming shows</p>
          </div>
        )}
      </section>
      )}

      {/* Past Shows */}
      {renderSection('past-shows',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Past Shows</h2>
        {pastShows.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="hidden sm:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue</th>
                  <th className="hidden lg:table-cell text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Capacity</th>
                  <th className="text-right py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Tickets</th>
                  <th className="hidden sm:table-cell text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">% Sold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pastShows.map((show) => {
                  const ticketsSold = show.tix_sold || show.ticket_count || 0
                  const capacity = Number(show.sellable_cap) || show.capacity || 0
                  const percentSold = capacity > 0 ? Math.round((ticketsSold / capacity) * 100) : 0
                  
                  return (
                    <tr key={show.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">{shortDateDisplay(show.date || show.perf_date)}</td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm text-gray-600">{show.city}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-900">{show.venue}</td>
                      <td className="hidden lg:table-cell py-3 px-4 text-sm text-right text-gray-600">{capacity > 0 ? capacity.toLocaleString() : '-'}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-right text-gray-900 font-medium">{ticketsSold.toLocaleString()}</td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm text-right text-gray-900 font-medium">
                        {capacity > 0 ? `${percentSold}%` : '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-sm text-gray-500">No past shows</p>
          </div>
        )}
      </section>
      )}

      {/* Offers Expiring Next */}
      {renderSection('offers',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Offers Expiring Next</h2>
        {offersExpiringNext.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="hidden sm:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Capacity</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Guarantee</th>
                  <th className="hidden xl:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Bonus</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Expires</th>
                  <th className="hidden sm:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offersExpiringNext.map((show: Show) => {
                  const daysUntilExpires = show.offer_expires 
                    ? Math.ceil(((parseLocalDate(show.offer_expires) || new Date()).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : null
                  
                  return (
                    <tr key={show.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">{shortDateDisplay(show.date || show.perf_date)}</td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm text-gray-600">{show.city}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-900">{show.venue}</td>
                      <td className="hidden lg:table-cell py-3 px-4 text-sm text-gray-600">{show.sellable_cap || show.capacity || '-'}</td>
                      <td className="hidden md:table-cell py-3 px-4 text-sm text-gray-900">{show.guarantee || '-'}</td>
                      <td className="hidden xl:table-cell py-3 px-4 text-sm text-gray-600">{show.bonus || '-'}</td>
                      <td className="hidden lg:table-cell py-3 px-4 text-sm text-gray-600">{show.description || '-'}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">
                        {show.offer_expires ? (
                          <span className={daysUntilExpires && daysUntilExpires <= 3 ? 'text-red-600 font-medium' : ''}>
                            {daysUntilExpires !== null && daysUntilExpires >= 0 
                              ? `${daysUntilExpires}d`
                              : shortDateDisplay(show.offer_expires)
                            }
                          </span>
                        ) : '-'}
                      </td>
                      <td className="hidden sm:table-cell py-3 px-4 text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          {show.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-sm text-gray-500">No pending offers</p>
          </div>
        )}
      </section>
      )}

      {/* Music Performance */}
      {renderSection('music',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Music Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Next Release</div>
            {nextRelease ? (
              <>
                <div className="text-lg font-semibold text-gray-900 mb-1">
                  {nextRelease.artist_name || 'Unknown Artist'} - {nextRelease.title || nextRelease.song_title || 'Untitled'}
                </div>
                <div className="text-sm text-gray-600">
                  Releases in {nextReleaseDays} {nextReleaseDays === 1 ? 'day' : 'days'}
                </div>
              </>
            ) : (
              <div className="text-lg font-semibold text-gray-900">No upcoming releases</div>
            )}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Latest Release</div>
            {latestRelease ? (
              <>
                <div className="text-lg font-semibold text-gray-900 mb-1">
                  {latestRelease.artist_name || 'Unknown Artist'} - {latestRelease.title || latestRelease.song_title || 'Untitled'}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{(latestRelease.streams_manual_override ?? latestRelease.streams_total ?? 0).toLocaleString()} total streams</span>
                  {latestRelease.streams_today !== undefined && (
                    <span>• {latestRelease.streams_today.toLocaleString()} yesterday</span>
                  )}
                </div>
              </>
            ) : (
              <div className="text-lg font-semibold text-gray-900">No latest releases</div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Tickets */}
      {renderSection('tickets',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Tickets</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Unsold Tickets</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{openTickets.toLocaleString()}</div>
            <div className="text-xs text-gray-500">All shows</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Tickets Sold This Week</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{ticketsWeek.toLocaleString()}</div>
            <div className="text-xs text-gray-500">Last 7 days</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Sold Out Shows YTD</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{soldOutShowsYTD}</div>
            <div className="text-xs text-gray-500">Shows at full capacity this year</div>
          </div>
        </div>
      </section>
      )}

      {/* Commerce Snapshot */}
      {renderSection('commerce',
      <section className="mb-6 sm:mb-10">
        <h2 className="text-sm font-medium mb-4 text-gray-900">Commerce Snapshot</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders Today</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersToday.length}</div>
            <div className="text-xs text-gray-500">Revenue: ${revenueCommerceToday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders This Week</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersWeek.length}</div>
            <div className="text-xs text-gray-500">Revenue: ${revenueCommerceWeek.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Orders This Month</div>
            <div className="text-2xl font-semibold text-gray-900 mb-1">{ordersMonth.length}</div>
            <div className="text-xs text-gray-500">Revenue: ${revenueCommerceMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </section>
      )}

      {/* Finance Snapshot */}
      {renderSection('finance',
      <section className="mb-6 sm:mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-900">Finance Snapshot</h2>
          <Link to="/finance" className="text-xs text-gray-500 hover:text-gray-900">
            View finance →
          </Link>
        </div>
        {recentFinancialEntries.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Vendor</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="text-right py-3 px-2 sm:px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentFinancialEntries.map((entry: FinancialEntry) => (
                  <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">{shortDateDisplay(entry.date)}</td>
                    <td className="hidden md:table-cell py-3 px-4 text-sm text-gray-600">{entry.vendor || entry.source || '-'}</td>
                    <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">{entry.description || '-'}</td>
                    <td className="py-3 px-2 sm:px-4 text-sm text-gray-600">
                      <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${
                        entry.type === 'revenue' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {entry.type === 'revenue' ? 'Rev' : 'Exp'}
                      </span>
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-right text-gray-900 font-medium">
                      ${(entry.invoice_total || entry.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td className="hidden lg:table-cell py-3 px-4 text-sm text-gray-600">{entry.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-sm text-gray-500">No financial entries</p>
          </div>
        )}
      </section>
      )}
      </div> {/* End of flex container */}
    </div>
  )
}
